// Scanner (Redesign Schritt 4, Spec 4.2)
// Modus-Tabs · Markt-Chips (Auto nach Uhrzeit Berlin) · Filterzeile mit Einstellungen (serverseitig
// je Modus) · Signalliste als Zeilen · Regime-Leiste · Scan starten / Fuer Claude.
// Antippen des Scores kopiert das Einzel-Briefing, langes Druecken legt das Signal in die Ablage.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../api/client";
import { HeaderActions } from "../shell/HeaderSlot";
import { fmtBriefingDetail, fmtBriefingList, sdFactor } from "../scanner/briefing";
import { ALL_MARKETS, MARKET_LABEL, Market, autoMarkets, berlinHHMM, marketOf } from "../scanner/markets";

type Mode = "swing" | "longterm" | "intraday";
const MODES: { key: Mode; label: string }[] = [
  { key: "swing", label: "Swing" }, { key: "longterm", label: "Longterm" }, { key: "intraday", label: "Intraday" },
];

interface Filters { min_score: number; min_rrr: number; min_vol: number; min_adx: number; hide_earnings_days: number; }
const DEFAULT_FILTERS: Filters = { min_score: 85, min_rrr: 1.5, min_vol: 1.0, min_adx: 25, hide_earnings_days: 3 };
const LONG_PRESS_MS = 550;
const STALE_MS = 2 * 60 * 60 * 1000;
// 28.10.2026: ohne Timeout haengt ein Request bei abgerissener Mobilverbindung ewig ("Laedt ...")
const REQ_TIMEOUT_MS = 20_000;
const errText = (e: any): string => {
  if (e?.code === "ECONNABORTED" || e?.code === "ETIMEDOUT") return "Zeitüberschreitung (keine Antwort nach 20 s)";
  if (e?.response?.status) return `Serverfehler HTTP ${e.response.status}`;
  if (e?.message === "Network Error") return "Keine Verbindung zum Server";
  return e?.message || "Unbekannter Fehler";
};

// ── Werte je Signal (Feldnamen aus /scanner/signals bzw. engine/*.py) ──
const num = (x: any): number | null => (x === null || x === undefined || x === "" || isNaN(Number(x)) ? null : Number(x));
const isIntraday = (s: any) => s.signal_type === "intraday" || !!s.scoring_details?.criteria;
const volOf = (s: any) => isIntraday(s) ? num(s.scoring_details?.rvol)
  : num(s.market_context?.sym_vol_ratio ?? sdFactor(s.scoring_details, "Vol_Ratio"));
const adxOf = (s: any) => num(s.trade_levels?.adx_live ?? s.market_context?.symbol_adx);
// Struktur-RRR aus trade_levels (nicht das konstante Kopf-rr). Intraday hat keine
// trade_levels, dort ist rr aus Entry/SL/TP der echte Wert.
const rrrOf = (s: any) => num(s.trade_levels?.rrr ?? (isIntraday(s) ? s.rr : null));
const earningsOf = (s: any): { days: number | null; date: string | null } => {
  const e = s.scoring_details?._earnings;
  return { days: num(e?.days_until), date: e?.earnings_date || null };
};

const de = (x: number | null, d = 2) => x === null ? "–" : x.toLocaleString("de-DE", { minimumFractionDigits: d, maximumFractionDigits: d });
const ddmm = (iso: string | null) => {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.` : "";
};
function scanLine(iso: string | null, now: number): { text: string; stale: boolean } {
  if (!iso) return { text: "Scanzeit unbekannt", stale: true };
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { text: String(iso), stale: true };
  const fmt = new Intl.DateTimeFormat("de-DE", { timeZone: "Europe/Berlin", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  const p = Object.fromEntries(fmt.formatToParts(d).map(x => [x.type, x.value]));
  const diff = Math.max(0, now - d.getTime());
  const min = Math.round(diff / 60000);
  const ago = min < 60 ? `vor ${min} Min` : `vor ${Math.round(min / 60)} Std`;
  return { text: `${p.day}.${p.month}. ${p.hour}:${p.minute} · ${ago}`, stale: diff > STALE_MS };
}

async function copyText(text: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(text); return true; } catch { /* Rueckfall unten */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand("copy"); document.body.removeChild(ta); return ok;
  } catch { return false; }
}

const readLocal = (k: string, fb: string) => { try { return localStorage.getItem(k) ?? fb; } catch { return fb; } };
const writeLocal = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* egal */ } };

// ── Icons (Inline-SVG, 1,5 px, currentColor) ──
const IconSearch = () => (<svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true"><circle cx="8" cy="8" r="5" fill="none" stroke="currentColor" strokeWidth="1.5" /><path d="M12 12l3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>);
const IconSliders = () => (<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M2 4h7M12 4h2M2 12h2M7 12h7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /><circle cx="10.5" cy="4" r="1.6" fill="none" stroke="currentColor" strokeWidth="1.4" /><circle cx="5.5" cy="12" r="1.6" fill="none" stroke="currentColor" strokeWidth="1.4" /></svg>);
const IconCopy = () => (<svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><rect x="3.5" y="3.5" width="7" height="7" rx="1.2" fill="none" stroke="currentColor" strokeWidth="1.2" /><path d="M8.5 1.5h-6a1 1 0 0 0-1 1v6" fill="none" stroke="currentColor" strokeWidth="1.2" /></svg>);
const IconBookmark = () => (<svg width="12" height="12" viewBox="0 0 12 12" aria-label="in der Ablage"><path d="M3 1.5h6v9L6 8.5l-3 2z" fill="currentColor" /></svg>);

// ── Einstellungen ──
function Stepper({ label, value, step, min, max, digits, onChange }:
  { label: string; value: number; step: number; min: number; max: number; digits: number; onChange: (v: number) => void }) {
  const set = (v: number) => onChange(Math.min(max, Math.max(min, Math.round(v / step) * step)));
  return (
    <div className="sc-set-row">
      <span>{label}</span>
      <div className="sc-stepper">
        <button aria-label={`${label} weniger`} onClick={() => set(value - step)}>−</button>
        <span className="mono">{de(value, digits)}</span>
        <button aria-label={`${label} mehr`} onClick={() => set(value + step)}>+</button>
      </div>
    </div>
  );
}

function useFilters(mode: Mode) {
  const key = `scanner_filters_${mode}`;
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["settings", key],
    queryFn: () => apiClient.get(`/settings/${key}`).then(r => ({ ...DEFAULT_FILTERS, ...(r.data?.value || {}) }) as Filters),
    staleTime: Infinity,
  });
  const filters = data || DEFAULT_FILTERS;
  const timer = useRef<number | undefined>(undefined);
  const update = useCallback((patch: Partial<Filters>) => {
    const next = { ...filters, ...patch };
    qc.setQueryData(["settings", key], next);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => { apiClient.put(`/settings/${key}`, { value: next }).catch(() => {}); }, 600);
  }, [filters, key, qc]);
  return { filters, update };
}

// ── Details beim Antippen (kompakt, nur vorhandene Werte) ──
const pz = (x: any) => { const v = num(x); if (v === null) return "–"; const a = Math.abs(v);
  return v.toLocaleString("de-DE", { maximumFractionDigits: a >= 100 ? 2 : a >= 1 ? 3 : 4 }); };
const pct = (x: any) => { const v = num(x); return v === null ? "" : ` (${v > 0 ? "+" : ""}${v.toLocaleString("de-DE", { maximumFractionDigits: 1 })} %)`; };
function SignalDetails({ s }: { s: any }) {
  const tl = s.trade_levels, mc = s.market_context || {}, e = earningsOf(s);
  const strategies = String(s.strategy || "").split(",").map((x: string) => x.trim().replace(/_/g, " ")).filter(Boolean).join(", ");
  const lines: [string, string][] = [];
  if (strategies) lines.push(["Strategie", strategies]);
  if (tl) lines.push(["Kurs", `${pz(tl.price_ref)} · SL ${pz(tl.sl)}${pct(tl.sl_pct)} · TP ${pz(tl.tp)}${pct(tl.tp_pct)}${tl.entry_quality ? " · " + tl.entry_quality : ""}`]);
  else if (num(s.entry) !== null) lines.push(["Kurs", `Entry ${pz(s.entry)} · SL ${pz(s.sl)} · TP ${pz(s.tp)}`]);
  if (s.entry_trigger) lines.push(["Trigger", `${pz(s.entry_trigger)}${s.entry_trigger_type ? " · " + String(s.entry_trigger_type).replace(/_/g, " ") : ""}`]);
  if (mc.index) lines.push(["Markt", `${mc.index} ${String(mc.regime || "").toUpperCase()}${num(mc.regime_adx) !== null ? " · ADX " + Math.round(mc.regime_adx) : ""}${mc.trend ? " · " + (mc.trend === "bull" ? "über EMA200" : "unter EMA200") : ""}`]);
  if (mc.symbol_regime) lines.push(["Symbol", `${String(mc.symbol_regime).toUpperCase()}${num(mc.sym_rsi) !== null ? " · RSI " + Math.round(mc.sym_rsi) : ""}`]);
  if (e.date) lines.push(["Earnings", `${ddmm(e.date)}${e.days !== null ? " · in " + e.days + " T" : ""}`]);
  if (tl?.warnings?.length) lines.push(["Hinweis", tl.warnings.join(" · ")]);
  return (
    <div className="sc-details" onPointerDown={ev => ev.stopPropagation()} onClick={ev => ev.stopPropagation()}>
      <dl>{lines.map(([k, v]) => <div key={k}><dt>{k}</dt><dd className="mono">{v}</dd></div>)}</dl>
      <div className="sc-links">
        {s.tv_link && <a href={s.tv_link} target="_blank" rel="noopener noreferrer">TradingView ↗</a>}
        <span>{s.exchange}{s.currency ? " · " + s.currency : ""}</span>
      </div>
    </div>
  );
}

// ── Signalzeile ──
function SignalRow({ s, now, inAblage, onCopy, onLongPress }:
  { s: any; now: number; inAblage: boolean; onCopy: (s: any) => void; onLongPress: (s: any) => void }) {
  const [open, setOpen] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const start = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);
  const cancel = () => { window.clearTimeout(timer.current); start.current = null; };
  const long = s.direction !== "short";
  const scan = scanLine(s.scanned_at_iso, now);
  const vol = volOf(s), adx = adxOf(s), rrr = rrrOf(s), earn = earningsOf(s);
  const meta = [vol !== null ? `${isIntraday(s) ? "RVOL" : "Vol"} ${de(vol)}` : null,
                adx !== null ? `ADX ${Math.round(adx)}` : null,
                rrr !== null ? `RRR ${de(rrr)}` : null].filter(Boolean).join(" · ");
  const fill = Math.max(0, Math.min(1, ((num(s.score) ?? 60) - 60) / 40));
  const showEarn = earn.days !== null && earn.days >= 0 && earn.days <= 14 && earn.date;

  return (
    <div className={`sc-item${open ? " open" : ""}`}>
    <div className="sc-row" role="button" tabIndex={0} aria-expanded={open}
      onClick={() => { if (!fired.current) setOpen(o => !o); fired.current = false; }}
      onKeyDown={e => { if (e.key === "Enter") setOpen(o => !o); }}
      onPointerDown={e => {
        fired.current = false; start.current = { x: e.clientX, y: e.clientY };
        timer.current = window.setTimeout(() => { fired.current = true; onLongPress(s); }, LONG_PRESS_MS);
      }}
      onPointerMove={e => {
        if (start.current && (Math.abs(e.clientX - start.current.x) > 10 || Math.abs(e.clientY - start.current.y) > 10)) cancel();
      }}
      onPointerUp={cancel} onPointerLeave={cancel} onPointerCancel={cancel}
      onContextMenu={e => e.preventDefault()}>
      <span className={`sc-dir ${long ? "up" : "down"}`} aria-label={long ? "Long" : "Short"}>{long ? "▲" : "▼"}</span>
      <div className="sc-body">
        <div className="sc-name">
          <span className="sc-company">{s.company_name || s.symbol}</span>
          <span className="sc-sym mono">{s.symbol}</span>
          {inAblage && <span className="sc-mark"><IconBookmark /></span>}
        </div>
        <div className={`sc-time${scan.stale ? " stale" : ""}`}>{scan.text}</div>
        <div className="sc-meta mono">
          {meta || "–"}
          {showEarn && <span className="sc-badge">Earnings {ddmm(earn.date)}</span>}
          {s.market_context?.against_index_trend && <span className="sc-badge">gegen Markttrend</span>}
        </div>
      </div>
      <button className="sc-score" aria-label={`Score ${s.score}, Briefing kopieren`}
        onPointerDown={e => e.stopPropagation()}
        onClick={e => { e.stopPropagation(); onCopy(s); }}>
        <span className="sc-score-num mono"><IconCopy /> {Math.round(num(s.score) ?? 0)}</span>
        <span className="sc-bar"><span style={{ width: `${fill * 100}%` }} /></span>
      </button>
    </div>
    {open && <SignalDetails s={s} />}
    </div>
  );
}

export default function ScannerPage() {
  const qc = useQueryClient();
  const [mode, setModeState] = useState<Mode>(() => (readLocal("scanner_mode", "swing") as Mode) || "swing");
  const setMode = (m: Mode) => { setModeState(m); writeLocal("scanner_mode", m); };
  const [auto, setAuto] = useState(true);
  const [manual, setManual] = useState<Market[]>([]);
  const [now, setNow] = useState(Date.now());
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [panelOpen, setPanelOpen] = useState(false);
  const [toast, setToast] = useState<{ text: string; undo?: () => void } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [scanState, setScanState] = useState<"idle" | "running">("idle");
  const toastTimer = useRef<number | undefined>(undefined);
  const scanLock = useRef(false);

  useEffect(() => { const t = window.setInterval(() => setNow(Date.now()), 30_000); return () => window.clearInterval(t); }, []);

  const { filters, update } = useFilters(mode);

  const { data, isLoading, isError, error, failureCount, refetch } = useQuery({
    queryKey: ["signals", mode, filters.min_score],
    queryFn: ({ signal }) => apiClient.get("/scanner/signals", { params: { mode, min_score: filters.min_score }, signal, timeout: REQ_TIMEOUT_MS }).then(r => r.data),
    refetchInterval: 60_000,
    retry: 1,
  });
  const { data: ablage = [] } = useQuery({
    queryKey: ["ablage", "offen"],
    queryFn: () => apiClient.get("/ablage", { params: { status: "offen" } }).then(r => r.data),
  });
  const ablageKeys = useMemo(() => new Set((Array.isArray(ablage) ? ablage : []).map((a: any) => `${a.symbol}|${a.mode}`)), [ablage]);

  const markets: Market[] = auto ? autoMarkets(new Date(now)) : manual;
  const all: any[] = Array.isArray(data?.signals) ? data.signals : [];
  const inMarket = all.filter(s => markets.includes(marketOf(s.exchange, s.index_member)));
  const q = query.trim().toLowerCase();
  const shown = inMarket.filter(s => {
    if ((num(s.score) ?? 0) < filters.min_score) return false;
    // Filter greifen nur, wenn der Wert vorhanden ist (Intraday hat z. B. keinen ADX)
    const rrr = rrrOf(s), vol = volOf(s), adx = adxOf(s);
    if (rrr !== null && rrr < filters.min_rrr - 1e-9) return false;
    if (vol !== null && vol < filters.min_vol - 1e-9) return false;
    if (adx !== null && adx < filters.min_adx - 1e-9) return false;
    const e = earningsOf(s);
    if (filters.hide_earnings_days > 0 && e.days !== null && e.days >= 0 && e.days <= filters.hide_earnings_days) return false;
    if (q && !(s.symbol.toLowerCase().includes(q) || (s.company_name || "").toLowerCase().includes(q))) return false;
    return true;
  }).sort((a, b) => (num(b.score) ?? 0) - (num(a.score) ?? 0));

  // Regime-Leiste: Indizes aus dem Marktkontext der Signale (engine/base.py get_market_context)
  const regimes = useMemo(() => {
    const m = new Map<string, { regime: string; adx: number | null; n: number }>();
    inMarket.forEach(s => {
      const mc = s.market_context;
      if (!mc?.index) return;
      const cur = m.get(mc.index);
      if (cur) cur.n++;
      else m.set(mc.index, { regime: String(mc.regime || "–").toUpperCase(), adx: num(mc.regime_adx), n: 1 });
    });
    return Array.from(m.entries()).sort((a, b) => b[1].n - a[1].n);
  }, [inMarket]);

  const showToast = (text: string, undo?: () => void) => {
    window.clearTimeout(toastTimer.current);
    setToast({ text, undo });
    toastTimer.current = window.setTimeout(() => setToast(null), 5000);
  };

  const toggleMarket = (m: Market) => {
    const base = auto ? autoMarkets(new Date(now)) : manual;
    const next = base.includes(m) ? base.filter(x => x !== m) : [...base, m];
    setAuto(false); setManual(next);
  };

  const onCopy = async (s: any) => {
    const ok = await copyText(fmtBriefingDetail(s));
    setCopied(ok ? `${s.symbol} kopiert` : "Kopieren fehlgeschlagen");
    window.setTimeout(() => setCopied(null), 1500);
  };

  const onLongPress = async (s: any) => {
    if (navigator.vibrate) navigator.vibrate(15);
    try {
      const r = await apiClient.post("/ablage", { symbol: s.symbol, mode: s.signal_type });
      qc.invalidateQueries({ queryKey: ["ablage"] });
      const id = r.data?.id;
      showToast(`${s.company_name || s.symbol} in die Ablage gelegt`, async () => {
        try { await apiClient.delete(`/ablage/${id}`); } catch { /* schon weg */ }
        qc.invalidateQueries({ queryKey: ["ablage"] }); setToast(null);
      });
    } catch {
      showToast("Ablegen fehlgeschlagen");
    }
  };

  const onCopyAll = async () => {
    const ok = await copyText(fmtBriefingList(shown));
    showToast(ok ? `${shown.length} Signale kopiert` : "Kopieren fehlgeschlagen");
  };

  // Scan starten: Anfrage an trading-scanner, dann pollen bis scanned_at sich aendert
  const onScan = async () => {
    if (mode === "longterm" || scanState === "running" || scanLock.current) return;
    scanLock.current = true;
    setScanState("running");
    try {
      const before = (await apiClient.get("/scanner/last-scan", { params: { mode }, timeout: REQ_TIMEOUT_MS })).data?.last_scan ?? null;
      await apiClient.post(mode === "swing" ? "/scanner/trigger-swing-scan" : "/scanner/trigger-intraday-scan", null, { timeout: REQ_TIMEOUT_MS });
      const t0 = Date.now();
      while (Date.now() - t0 < 180_000) {
        await new Promise(r => setTimeout(r, 5000));
        let cur: any = null;
        try { cur = (await apiClient.get("/scanner/last-scan", { params: { mode }, timeout: REQ_TIMEOUT_MS })).data?.last_scan ?? null; }
        catch { continue; } // einzelner Poll-Fehler (Mobilfunk) bricht den Scan nicht ab
        if (cur && cur !== before) { await refetch(); showToast("Scan fertig"); return; }
      }
      showToast("Keine neuen Ergebnisse (Markt evtl. geschlossen)");
    } catch {
      showToast("Scan konnte nicht gestartet werden");
    } finally {
      scanLock.current = false;
      setScanState("idle");
    }
  };

  const summary = `Score ≥ ${filters.min_score} · RRR ≥ ${de(filters.min_rrr, 1)} · Vol ≥ ${de(filters.min_vol, 1)} · ADX ≥ ${filters.min_adx}`;

  return (
    <div className="sc">
      <HeaderActions>
        <button className="nx-icon-btn" aria-label="Suchen" aria-expanded={searchOpen}
          onClick={() => { setSearchOpen(o => !o); if (searchOpen) setQuery(""); }}><IconSearch /></button>
      </HeaderActions>

      {searchOpen && (
        <input className="sc-search" autoFocus placeholder="Name oder Symbol" value={query}
          onChange={e => setQuery(e.target.value)} aria-label="Signale durchsuchen" />
      )}

      <div className="sc-modes" role="tablist">
        {MODES.map(m => (
          <button key={m.key} role="tab" aria-selected={mode === m.key}
            className={`nx-subtab${mode === m.key ? " active" : ""}`} onClick={() => setMode(m.key)}>{m.label}</button>
        ))}
      </div>

      <div className="sc-chips">
        <button className={`sc-chip${auto ? " auto" : ""}`} onClick={() => setAuto(true)} aria-pressed={auto}>
          Auto · {berlinHHMM(new Date(now))}
        </button>
        {ALL_MARKETS.map(m => (
          <button key={m} className={`sc-chip${markets.includes(m) ? " on" : ""}`} aria-pressed={markets.includes(m)}
            onClick={() => toggleMarket(m)}>{MARKET_LABEL[m]}</button>
        ))}
      </div>

      <button className="sc-filter mono" onClick={() => setPanelOpen(o => !o)} aria-expanded={panelOpen}>
        <IconSliders /><span>{summary}</span><span>{panelOpen ? "▴" : "▾"}</span>
      </button>
      {panelOpen && (
        <div className="sc-panel">
          <Stepper label="Min. Score" value={filters.min_score} step={1} min={50} max={100} digits={0} onChange={v => update({ min_score: v })} />
          <Stepper label="Min. RRR" value={filters.min_rrr} step={0.1} min={0} max={5} digits={1} onChange={v => update({ min_rrr: v })} />
          <Stepper label="Min. Volumen" value={filters.min_vol} step={0.1} min={0} max={5} digits={1} onChange={v => update({ min_vol: v })} />
          <Stepper label="Min. ADX" value={filters.min_adx} step={1} min={0} max={60} digits={0} onChange={v => update({ min_adx: v })} />
          <label className="sc-set-row">
            <span>Earnings ≤ 3 Tage ausblenden</span>
            <input type="checkbox" checked={filters.hide_earnings_days > 0}
              onChange={e => update({ hide_earnings_days: e.target.checked ? 3 : 0 })} />
          </label>
          <div className="sc-hint">Gilt für {MODES.find(m => m.key === mode)?.label}, auf allen Geräten. Filter greifen nur, wo das Signal den Wert hat.</div>
        </div>
      )}

      <div className="sc-count">
        <span>{shown.length} von {inMarket.length} Signalen</span>
        <span>Score ↓</span>
      </div>

      <div className="sc-list">
        {scanState === "running" && <div className="sc-empty">Scan läuft … Ergebnisse kommen automatisch.</div>}
        {isLoading && <div className="sc-empty">{failureCount > 0 ? "Lädt … (zweiter Versuch)" : "Lädt …"}</div>}
        {isError && (
          <div className="sc-empty">
            {data ? "Aktualisierung fehlgeschlagen" : "Signale konnten nicht geladen werden"}: {errText(error)}
            <div><button className="sc-retry" onClick={() => refetch()}>Erneut laden</button></div>
          </div>
        )}
        {!isLoading && !isError && shown.length === 0 && (
          <div className="sc-empty">{markets.length === 0 ? "Kein Markt gewählt." : "Keine Signale für diese Filter."}</div>
        )}
        {shown.map(s => (
          <SignalRow key={`${s.symbol}|${s.signal_type}`} s={s} now={now}
            inAblage={ablageKeys.has(`${s.symbol}|${s.signal_type}`)} onCopy={onCopy} onLongPress={onLongPress} />
        ))}
      </div>

      <div className="sc-dock">
        {(toast || copied) && (
          <div className="sc-toast" role="status">
            <span>{copied || toast?.text}</span>
            {!copied && toast?.undo && <button onClick={toast.undo}>Rückgängig</button>}
          </div>
        )}
        {regimes.length > 0 && (
          <div className="sc-regimes mono">
            {regimes.map(([idx, r]) => (
              <span key={idx}>{idx} {r.regime}{r.adx !== null ? ` · ADX ${Math.round(r.adx)}` : ""}</span>
            ))}
          </div>
        )}
        <div className="sc-actions">
          <button onClick={onScan} disabled={mode === "longterm" || scanState === "running"}
            title={mode === "longterm" ? "Longterm läuft 2× täglich automatisch" : "Scan für offene Märkte"}>
            {scanState === "running" ? "Läuft …" : "Scan starten"}
          </button>
          <button onClick={onCopyAll} disabled={shown.length === 0}>Für Claude · {shown.length}</button>
        </div>
      </div>
    </div>
  );
}
